import {
  ErrorHandler,
  inject,
  Injectable,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {UrlFunctions} from '../../../../../common/functions/url';
import {Links} from '../../../../../common/constants/links';
import {Constants} from '../../../../../common/constants/constants';
import {JwtService} from '../../../auth/jwt/jwt.service';
import {IframeAuthService} from '../../../auth/iframe-auth/iframe-auth.service';
import {PublicEntrypointService} from '../../../auth/public-entrypoint/public-entrypoint.service';
import {BrowserSessionStorageService} from '../../storage/browser-session-storage/browser-session-storage.service';

interface IWrappedError {
  message?: unknown;
  rejection?: IWrappedError;
  ngOriginalError?: IWrappedError;
}

const CHUNK_LOAD_ERROR_PATTERNS: ReadonlyArray<RegExp> = [
  /Loading chunk \d+ failed/,
  /ChunkLoadError/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
];

@Injectable()
export class SngLoadingChunkErrorHandler implements ErrorHandler {

  private jwtService: JwtService = inject(JwtService);

  private iframeAuthService: IframeAuthService = inject(IframeAuthService);

  private publicEntrypointService: PublicEntrypointService = inject(PublicEntrypointService);

  private browserSessionStorageService: BrowserSessionStorageService = inject(BrowserSessionStorageService);

  private sentryHandler: ErrorHandler = Sentry.createErrorHandler();

  private lastReloadTimestamp: number = Constants.CHUNK_RELOAD_NEVER;

  /**
   * Method: Checks if the user has access to the application.
   * @private
   */
  private userHasAccess(): boolean {
    return !!this.jwtService.hasAccessToken()
      || !!this.iframeAuthService.hasIframeToken()
      || !!this.publicEntrypointService.hasJwtTokenPublicEntryPoint()
      || !!this.publicEntrypointService.isPublicEntryPointPage()
      || UrlFunctions.isPageByRouteUrl(Links.ERROR_LINK_URL);
  }

  /**
   * Method: Handles errors by reloading the page on chunk load failures,
   * otherwise delegates to Sentry so the event (with breadcrumbs) reaches the dashboard.
   * @param error
   */
  public handleError(error: any): void {
    if (this.isChunkLoadError(error) && this.userHasAccess() && this.markReload()) {
      this.reloadPage();
      return;
    }

    this.sentryHandler.handleError(error);
  }

  /**
   * Method: Match the lazy-chunk failure message of every supported browser
   * @param error - The error passed to the global handler, possibly wrapping the original
   */
  private isChunkLoadError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
      return false;
    }
    const wrapped: IWrappedError = error;
    const messages: string[] = [wrapped.message, wrapped.rejection?.message, wrapped.ngOriginalError?.message]
      .filter((message: unknown): message is string => typeof message === 'string');
    return messages.some((message: string): boolean => CHUNK_LOAD_ERROR_PATTERNS
      .some((pattern: RegExp): boolean => pattern.test(message)));
  }

  /**
   * Method: Record the reload and refuse a second one inside the guard window
   */
  private markReload(): boolean {
    const now = Date.now();
    const lastReload = this.readLastReload();
    if (lastReload !== Constants.CHUNK_RELOAD_NEVER && now - lastReload < Constants.CHUNK_RELOAD_GUARD_MS) {
      return false;
    }
    this.storeLastReload(now);
    return true;
  }

  /**
   * Method: Read the latest known reload time from storage, falling back to memory
   */
  private readLastReload(): number {
    try {
      const storedValue = this.browserSessionStorageService.getItem(Constants.CHUNK_RELOAD_TIMESTAMP);
      const stored = Number(storedValue) || Constants.CHUNK_RELOAD_NEVER;
      this.lastReloadTimestamp = Math.max(stored, this.lastReloadTimestamp);
    } catch (_) {
      return this.lastReloadTimestamp;
    }
    return this.lastReloadTimestamp;
  }

  /**
   * Method: Store the reload time in memory first, then in browser storage
   * @param now - The timestamp of the reload that was just allowed
   */
  private storeLastReload(now: number): void {
    this.lastReloadTimestamp = now;
    try {
      this.browserSessionStorageService.setItem(Constants.CHUNK_RELOAD_TIMESTAMP, String(now));
    } catch (_) {
      return;
    }
  }

  /**
   * Method: Fetch a fresh index.html so the new chunk names replace the stale ones
   */
  protected reloadPage(): void {
    window.location.reload();
  }

}
